import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Home, Upload, Trash2, Search, RefreshCw, Download, Copy, Check, Folder } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { uploadImageFromUrl } from '../../utils/imageUploader';

interface FileObject {
  id: string;
  name: string;
  size: number;
  created_at: string;
  updated_at: string;
  last_accessed_at: string;
  metadata: Record<string, any>;
}

const ImageManager: React.FC = () => {
  const [files, setFiles] = useState<FileObject[]>([]);
  const [folders, setFolders] = useState<string[]>([]);
  const [currentFolder, setCurrentFolder] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFile, setSelectedFile] = useState<string | null>(null);
  const [copiedUrl, setCopiedUrl] = useState<string | null>(null);
  const [uploadUrl, setUploadUrl] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  // Set page title
  useEffect(() => {
    document.title = 'Quản lý Hình ảnh - VMon Cloud';
    return () => {
      document.title = 'VMon Cloud - Dịch vụ VPS và Hosting chất lượng cao';
    };
  }, []);

  // Load files and folders
  const loadFiles = async (folder: string = '') => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase.storage
        .from('vmon')
        .list(folder, {
          sortBy: { column: 'name', order: 'asc' }
        });

      if (error) throw error;

      // Separate files and folders
      const folderList: string[] = [];
      const fileList: FileObject[] = [];

      data?.forEach(item => {
        if (item.id) {
          fileList.push(item as FileObject);
        } else {
          folderList.push(item.name);
        }
      });

      setFiles(fileList);
      setFolders(folderList);
    } catch (error) {
      console.error('Error loading files:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    loadFiles(currentFolder);
  }, [currentFolder]);

  // Navigate to folder
  const navigateToFolder = (folder: string) => {
    const newPath = currentFolder ? `${currentFolder}/${folder}` : folder;
    setCurrentFolder(newPath);
  };

  // Navigate up one level
  const navigateUp = () => {
    if (!currentFolder) return;
    const parts = currentFolder.split('/');
    parts.pop();
    setCurrentFolder(parts.join('/'));
  };

  // Get public URL for a file
  const getPublicUrl = (fileName: string): string => {
    const path = currentFolder ? `${currentFolder}/${fileName}` : fileName;
    const { data } = supabase.storage
      .from('vmon')
      .getPublicUrl(path);
    return data.publicUrl;
  };

  // Copy URL to clipboard
  const copyToClipboard = (url: string) => {
    navigator.clipboard.writeText(url);
    setCopiedUrl(url);
    setTimeout(() => setCopiedUrl(null), 2000);
  };

  // Delete file
  const deleteFile = async (fileName: string) => {
    if (!confirm('Bạn có chắc chắn muốn xóa file này?')) return;
    
    try {
      const path = currentFolder ? `${currentFolder}/${fileName}` : fileName;
      const { error } = await supabase.storage
        .from('vmon')
        .remove([path]);

      if (error) throw error;
      
      // Refresh file list
      loadFiles(currentFolder);
    } catch (error) {
      console.error('Error deleting file:', error);
      alert('Có lỗi xảy ra khi xóa file');
    }
  };

  // Upload from URL
  const handleUploadFromUrl = async () => {
    if (!uploadUrl) return;
    
    setIsUploading(true);
    setUploadError(null);
    
    try {
      // Generate a filename from the URL
      const urlParts = uploadUrl.split('/');
      let fileName = urlParts[urlParts.length - 1];
      
      // Add timestamp to ensure uniqueness
      const timestamp = Date.now();
      const parts = fileName.split('.');
      const extension = parts.pop() || 'jpg';
      const name = parts.join('.');
      fileName = `${name}-${timestamp}.${extension}`;
      
      // Create the full path
      const path = currentFolder ? `${currentFolder}/${fileName}` : fileName;
      
      // Upload the image
      const publicUrl = await uploadImageFromUrl(uploadUrl, path);
      
      if (!publicUrl) {
        throw new Error('Failed to upload image');
      }
      
      // Reset form and refresh
      setUploadUrl('');
      loadFiles(currentFolder);
    } catch (error) {
      console.error('Error uploading from URL:', error);
      setUploadError('Có lỗi xảy ra khi tải lên: ' + (error instanceof Error ? error.message : String(error)));
    } finally {
      setIsUploading(false);
    }
  };

  // Filter files by search term
  const filteredFiles = files.filter(file => 
    file.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Format file size
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center">
              <Link
                to="/vmon-admin"
                className="mr-4 p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                title="Quay về Dashboard"
              >
                <Home className="h-5 w-5" />
              </Link>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Quản lý Hình ảnh</h1>
                <p className="text-gray-600">Quản lý hình ảnh trong Supabase Storage</p>
              </div>
            </div>
            <button
              onClick={() => loadFiles(currentFolder)}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors inline-flex items-center"
            >
              <RefreshCw className="h-5 w-5 mr-2" />
              Làm mới
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Breadcrumb and Search */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div className="flex items-center space-x-2 text-sm text-gray-600">
              <button 
                onClick={() => setCurrentFolder('')}
                className="hover:text-blue-600"
              >
                root
              </button>
              {currentFolder.split('/').filter(Boolean).map((folder, index, array) => (
                <React.Fragment key={index}>
                  <span>/</span>
                  <button 
                    onClick={() => setCurrentFolder(array.slice(0, index + 1).join('/'))}
                    className="hover:text-blue-600"
                  >
                    {folder}
                  </button>
                </React.Fragment>
              ))}
            </div>
            
            <div className="relative w-full md:w-auto">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
              <input
                type="text"
                placeholder="Tìm kiếm file..."
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 w-full md:w-64"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Upload from URL */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Tải lên từ URL</h3>
          <div className="flex flex-col md:flex-row gap-4">
            <input
              type="text"
              placeholder="Nhập URL hình ảnh..."
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
              value={uploadUrl}
              onChange={(e) => setUploadUrl(e.target.value)}
            />
            <button
              onClick={handleUploadFromUrl}
              disabled={isUploading || !uploadUrl}
              className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors inline-flex items-center"
            >
              {isUploading ? (
                <>
                  <RefreshCw className="h-5 w-5 mr-2 animate-spin" />
                  Đang tải lên...
                </>
              ) : (
                <>
                  <Upload className="h-5 w-5 mr-2" />
                  Tải lên
                </>
              )}
            </button>
          </div>
          {uploadError && (
            <div className="mt-2 text-sm text-red-600">
              {uploadError}
            </div>
          )}
        </div>

        {/* File Browser */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {isLoading ? (
            <div className="p-8 text-center">
              <RefreshCw className="h-8 w-8 text-blue-600 animate-spin mx-auto mb-4" />
              <p className="text-gray-600">Đang tải dữ liệu...</p>
            </div>
          ) : (
            <>
              {/* Folders */}
              {folders.length > 0 && (
                <div className="p-6 border-b border-gray-200">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Thư mục</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                    {currentFolder && (
                      <button
                        onClick={navigateUp}
                        className="flex flex-col items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                      >
                        <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center mb-2">
                          <Folder className="h-6 w-6 text-yellow-600" />
                        </div>
                        <span className="text-sm text-gray-700 truncate w-full text-center">..</span>
                      </button>
                    )}
                    
                    {folders.map(folder => (
                      <button
                        key={folder}
                        onClick={() => navigateToFolder(folder)}
                        className="flex flex-col items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                      >
                        <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center mb-2">
                          <Folder className="h-6 w-6 text-yellow-600" />
                        </div>
                        <span className="text-sm text-gray-700 truncate w-full text-center">{folder}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Files */}
              <div className="p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Files</h3>
                
                {filteredFiles.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-gray-500">Không có file nào trong thư mục này</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                    {filteredFiles.map(file => {
                      const publicUrl = getPublicUrl(file.name);
                      const isImage = /\.(jpg|jpeg|png|gif|webp|svg)$/i.test(file.name);
                      
                      return (
                        <div 
                          key={file.id} 
                          className={`border border-gray-200 rounded-lg overflow-hidden ${selectedFile === file.id ? 'ring-2 ring-blue-500' : ''}`}
                          onClick={() => setSelectedFile(selectedFile === file.id ? null : file.id)}
                        >
                          {isImage ? (
                            <div className="h-32 bg-gray-100 flex items-center justify-center">
                              <img 
                                src={publicUrl} 
                                alt={file.name} 
                                className="max-h-32 max-w-full object-contain"
                              />
                            </div>
                          ) : (
                            <div className="h-32 bg-gray-100 flex items-center justify-center">
                              <span className="text-4xl">📄</span>
                            </div>
                          )}
                          
                          <div className="p-3">
                            <p className="text-sm font-medium text-gray-900 truncate" title={file.name}>
                              {file.name}
                            </p>
                            <p className="text-xs text-gray-500">
                              {formatFileSize(file.size)}
                            </p>
                            
                            <div className="flex mt-2 space-x-2">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  copyToClipboard(publicUrl);
                                }}
                                className="p-1 text-gray-500 hover:text-blue-600"
                                title="Copy URL"
                              >
                                {copiedUrl === publicUrl ? (
                                  <Check className="h-4 w-4 text-green-500" />
                                ) : (
                                  <Copy className="h-4 w-4" />
                                )}
                              </button>
                              
                              <a
                                href={publicUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                className="p-1 text-gray-500 hover:text-blue-600"
                                title="Download"
                              >
                                <Download className="h-4 w-4" />
                              </a>
                              
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  deleteFile(file.name);
                                }}
                                className="p-1 text-gray-500 hover:text-red-600"
                                title="Delete"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ImageManager;